/** Canvas image quality roles — keep MASTER separate from display/delivery.
 *
 * SOURCE_ORIGINAL / GENERATED_MASTER → SiteAsset `original` (or dedicated output content)
 * DISPLAY_PREVIEW → SiteAsset `web` (lazy-derived ≤2000px)
 * THUMBNAIL → SiteAsset `thumbnail` (chips / picker only)
 * PROVIDER_DELIVERY → backend-only ephemeral/cached objects (never Canvas src)
 */

import { imageGenerationOutputContentUrl, siteAssetVariantUrl, virtualTryOnOutputContentUrl } from '../../../api'

export type CanvasImageQualityRole = 'generated_master' | 'display_preview' | 'thumbnail'

export function siteAssetDisplayPreviewUrl(workspaceId: string, assetId: string): string {
  return siteAssetVariantUrl(workspaceId, assetId, 'web')
}

export function siteAssetMasterUrl(workspaceId: string, assetId: string): string {
  return siteAssetVariantUrl(workspaceId, assetId, 'original')
}

/**
 * Canonical EDITABLE pixel source for the Canvas Image Editor — VistoeraTASK
 * -00075 root-cause fix. `siteAssetMasterUrl` (`.../variants/original`) is
 * correct for a passive `<img src>`/`<video src>` display: on Preview
 * (`ASSET_DELIVERY_MODE=presigned`) that route 302-redirects to a signed
 * `*.r2.cloudflarestorage.com` GET, which a plain `<img>` follows with no
 * CORS check at all. But `DualPaneCutoutEditor`'s canonical loader
 * (`mediaLoader.ts`'s `fetch(url, {credentials:'include'})` — the one call
 * that actually feeds the editable canvas/mask, not the progressive `<img>`
 * stand-in) enforces CORS on that *final* redirected response, and the R2
 * bucket has no CORS policy for the app origin — confirmed live: this made
 * every editable-source load fail with an opaque `TypeError: Failed to
 * fetch` while the identical URL rendered fine via `<img>`, leaving the
 * editor's error+retry state (`onSourceError`) stuck forever even though
 * pixels were visibly on screen. `.../variants/{role}/pixels` (new backend
 * route, `saas_assets.get_variant_pixels`) always streams bytes same-origin
 * through Render (`AssetSecurityClass.DYNAMIC_API`, never presign-
 * redirects) — CORS-safe for `fetch()`/canvas pixel access by construction,
 * same contract as the legacy `AssetChain` `chain_pixels` proxy the
 * Item-Processing editor path already relies on. Never use this for a
 * passive `<img>`/`<video>` tag — it deliberately gives up R2's CDN/edge
 * byte-serving for the one consumer that actually needs CORS-safe direct
 * access.
 */
export function siteAssetMasterPixelsUrl(workspaceId: string, assetId: string): string {
  return `${siteAssetVariantUrl(workspaceId, assetId, 'original')}/pixels`
}

/**
 * VistoeraTASK-00145 — fast, CORS-safe (`DYNAMIC_API`, same-origin stream,
 * never presign-redirected — identical delivery contract to
 * `siteAssetMasterPixelsUrl` above, just a different `role`) PLACEHOLDER
 * pixel source for the Canvas Image Editor's left/editable surface.
 *
 * Forensic root cause (VistoeraTASK-00145): the right "结果预览" pane
 * paints almost instantly because it is a plain `<img src>` pointed at the
 * `web` variant (≤2000px, `get_variant` 302-presign-redirects straight to
 * R2/CDN on Preview). The left editable surface's canonical loader
 * (`mediaLoader.ts`'s `fetch(..., {credentials:'include'})`) cannot use
 * that same presigned path (R2 bucket CORS is not configured for the app
 * origin, and — unlike a passive `<img>` — `fetch()` enforces CORS on the
 * *final* redirected response; see `siteAssetMasterPixelsUrl`'s own doc
 * comment for the full history), so it has always gone through the
 * `original` role's `DYNAMIC_API` proxy instead — i.e. the FULL, often
 * many-MB/8K-class original streamed byte-for-byte through Render. That
 * is the entire "right instant, left long wait" gap: not a duplicate
 * fetch, not a broken cache, not CORS failing outright (it eventually
 * succeeds) — it is genuinely downloading + decoding a much bigger file
 * over a slower (proxied, non-CDN) path before the editor can become
 * interactive at all.
 *
 * `.../variants/web/pixels` reuses the exact same generic backend route
 * (`saas_assets.get_variant_pixels`, parameterized by `role`) with
 * `role='web'` instead of `'original'` — same CORS-safe `DYNAMIC_API`
 * delivery class, but a MUCH smaller payload (the same bytes the right
 * pane's `<img>` already lazily materialized on the server, often already
 * warm by the time a user opens the editor). `DualPaneCutoutEditor` draws
 * this into the FULL-resolution image canvas (sized from the caller's own
 * already-known `SiteAsset.width/height` — see `knownSourceWidth`/
 * `knownSourceHeight` props) as a fast, upscaled first paint, then
 * silently swaps in the true full-resolution original once that slower
 * fetch resolves — see that component's source-load effect for the full
 * two-tier ("placeholder" -> "full") contract, including the export-time
 * guarantee that a save/export always waits for (or already has) the
 * true full-resolution pixels, never a permanently-downgraded quality.
 */
export function siteAssetDisplayPreviewPixelsUrl(workspaceId: string, assetId: string): string {
  return `${siteAssetVariantUrl(workspaceId, assetId, 'web')}/pixels`
}

/**
 * VistoeraTASK-00255 — decides whether `CanvasImageEditorOverlay` should
 * race the small `siteAssetDisplayPreviewPixelsUrl` ("web") placeholder
 * against the full `siteAssetMasterPixelsUrl` ("original") fetch, vs.
 * going straight to the (possibly much larger, slower) original alone.
 *
 * Extracted as a pure, independently testable function (was an inline
 * `useMemo` with a `Math.max(...) > 2400` size floor) after real-Preview
 * forensics found that floor effectively dead-coded the whole fast path
 * for the dominant real-world population: EVERY sampled completed
 * `generate_image` result (AI providers cap well under 2000px) and any
 * ordinary photo already resized to ≤2400px before upload. With the
 * floor removed, the ONLY requirement is that the caller already knows
 * the true pixel dimensions (needed to size the editable canvas up front
 * so brush/mask/crop coordinate math never has to rescale mid-session) —
 * see this module's and `CanvasImageEditorOverlay.tsx`'s own doc comments
 * for the full root-cause story and the bounded (~+300ms, previously
 * measured) worst-case cost this trades away the unbounded-stall risk.
 */
export function shouldUseEditorPlaceholderFastPath(
  knownSourceWidth: number | null | undefined,
  knownSourceHeight: number | null | undefined,
): boolean {
  return Boolean(knownSourceWidth && knownSourceHeight && knownSourceWidth > 0 && knownSourceHeight > 0)
}

export function siteAssetThumbnailUrl(workspaceId: string, assetId: string): string {
  return siteAssetVariantUrl(workspaceId, assetId, 'thumbnail')
}

/**
 * Canonical Canvas IMAGE chip / input-asset thumbnail.
 * Prefer SiteAsset thumbnail (never master). Fall back to try-on / image-gen
 * output content URLs only when no SiteAsset id is available.
 * Never use raw storage keys as <img src>.
 */
export function resolveCanvasImageThumbUrl(
  workspaceId: string,
  args: {
    entityId?: string | null
    generatedOutputId?: string | null
    nodeType?: string | null
  },
): string | null {
  if (!workspaceId) return null
  const entityId = args.entityId?.trim() || null
  if (entityId) return siteAssetThumbnailUrl(workspaceId, entityId)
  const outputId = args.generatedOutputId?.trim() || null
  if (!outputId) return null
  if (args.nodeType === 'generate_tryon') return virtualTryOnOutputContentUrl(workspaceId, outputId)
  return imageGenerationOutputContentUrl(workspaceId, outputId)
}

/** VISTOERA_GENERATE_VIDEO_IMAGE_ASSET_THUMBNAIL_HOVER_PREVIEW_PRIMITIVE_P1_V1
 * §P14 — the URL shown in a hover enlarged preview. Site assets use the
 * `web` display variant (≤2000px), never the tiny `thumbnail` chip src
 * upscaled, and never `original` unless no other variant exists. Gen-to-gen
 * sources without a materialized SiteAsset keep the same servable output
 * URL (there is no separate preview tier on the worker). */
export function resolveCanvasImageHoverPreviewUrl(
  workspaceId: string,
  args: {
    entityId?: string | null
    generatedOutputId?: string | null
    nodeType?: string | null
  },
): string | null {
  if (!workspaceId) return null
  const entityId = args.entityId?.trim() || null
  if (entityId) return siteAssetDisplayPreviewUrl(workspaceId, entityId)
  const outputId = args.generatedOutputId?.trim() || null
  if (!outputId) return null
  if (args.nodeType === 'generate_tryon') return virtualTryOnOutputContentUrl(workspaceId, outputId)
  return imageGenerationOutputContentUrl(workspaceId, outputId)
}

/** Canvas card / list display — never thumbnail for primary result areas. */
export function resolveTryOnCanvasDisplayUrl(
  workspaceId: string,
  outputAssetId: string | null | undefined,
  generatedOutputId: string | null | undefined,
): string | null {
  if (!workspaceId) return null
  if (outputAssetId) return siteAssetDisplayPreviewUrl(workspaceId, outputAssetId)
  if (generatedOutputId) return virtualTryOnOutputContentUrl(workspaceId, generatedOutputId)
  return null
}

/** Download / hi-res viewer master — never web/thumbnail/provider-delivery. */
export function resolveTryOnMasterUrl(
  workspaceId: string,
  outputAssetId: string | null | undefined,
  generatedOutputId: string | null | undefined,
): string | null {
  if (!workspaceId) return null
  if (outputAssetId) return siteAssetMasterUrl(workspaceId, outputAssetId)
  if (generatedOutputId) return virtualTryOnOutputContentUrl(workspaceId, generatedOutputId)
  return null
}

export function resolveImageGenCanvasDisplayUrl(
  workspaceId: string,
  outputId: string | null | undefined,
  siteAssetId: string | null | undefined,
): string | null {
  if (!workspaceId || !outputId) return null
  if (siteAssetId) return siteAssetDisplayPreviewUrl(workspaceId, siteAssetId)
  return imageGenerationOutputContentUrl(workspaceId, outputId)
}

export function resolveImageGenMasterUrl(
  workspaceId: string,
  outputId: string | null | undefined,
  siteAssetId: string | null | undefined,
): string | null {
  if (!workspaceId || !outputId) return null
  if (siteAssetId) return siteAssetMasterUrl(workspaceId, siteAssetId)
  return imageGenerationOutputContentUrl(workspaceId, outputId)
}
